import React from "react";
import DebouncedInput from "./DebounceInput";

// export function dateBetweenFilterFn(rows, id, filterValues) {
//   const sd = filterValues[0] ? new Date(filterValues[0]) : undefined;
//   const ed = filterValues[1] ? new Date(filterValues[1]) : undefined;
//   if (ed || sd) {
//     return rows.filter((r) => {
//       // format data
//       var dateAndHour = r.values[id].split(" ");
//       var [year, month, day] = dateAndHour[0].split("-");
//       var date = [day, month, year].join("/");
//       var hour = dateAndHour[1];
//       var formattedData = date + ", " + hour;

//       const cellDate = new Date(formattedData);

//       if (ed && sd) {
//         return cellDate >= sd && cellDate <= ed;
//       } else if (sd) {
//         return cellDate >= sd;
//       } else {
//         return cellDate <= ed;
//       }
//     });
//   } else {
//     return rows;
//   }
// }

export function dateBetweenFilterFn(row, columnId, value) {
  const rawDate = row.getValue(columnId);

  // If the column value is null or undefined, exclude it (adjust based on requirements)
  if (!rawDate) {
    console.log(`Excluding row "${row.id}" due to null/undefined value`);
    return false;
  }

  const [startValue, endValue] = value || [];

  // If no filter is applied (both start and end are undefined), include the row
  if (!startValue && !endValue) {
    console.log(`Including row "${row.id}" due to no filter being applied`);
    return true;
  }

  const date = new Date(rawDate);
  if (isNaN(date.getTime())) {
    console.error(`Invalid date format for column "${columnId}":`, rawDate);
    return false;
  }

  const start = startValue ? new Date(startValue) : null;
  const end = endValue ? new Date(endValue) : null;

  if (start && !end) {
    return date >= start;
  }

  if (!start && end) {
    return date <= end;
  }

  if (start && end) {
    return date >= start && date <= end;
  }

  // Default: Include the row
  return true;
}


export default function Filter({ column }) {
  const { filterVariant, filterOptions } = column.columnDef.meta || {};

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = React.useMemo(
    () =>
      Array.isArray(filterOptions)
        ? filterOptions
        : filterVariant === 'range' || filterVariant === 'date-range' || filterVariant === 'datetime-range'
        ? []
        : Array.from(column.getFacetedUniqueValues().keys())
          .sort()
          .slice(0, 5000),
    [column, filterOptions, filterVariant]
  );
  const selectOptions = React.useMemo(
    () => sortedUniqueValues.map(option => (
      option && typeof option === 'object'
        ? option
        : { value: option, label: option }
    )),
    [sortedUniqueValues]
  );

  return filterVariant === 'datetime-range' ? (
    <div className="d-flex gap-1">
      {['from', 'to'].map((bound, index) => (
        <input
          key={bound}
          type="datetime-local"
          step="1"
          aria-label={`${column.columnDef.header} ${bound} (local time)`}
          title={`${column.columnDef.header} ${bound} (local time)`}
          value={columnFilterValue?.[index] || ''}
          onChange={event => {
            const value = event.target.value;
            column.setFilterValue(old => {
              const next = [...(old || ['', ''])];
              next[index] = value;
              return next.some(Boolean) ? next : undefined;
            });
          }}
          className="form-control form-control-sm"
        />
      ))}
    </div>
  ) : filterVariant === 'range' ? (
    <div>
      <div className="d-flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] || '')}
          max={Number(column.getFacetedMinMaxValues()?.[1] || '')}
          value={columnFilterValue?.[0] || ''}
          onChange={value =>
            column.setFilterValue(old => [value, old?.[1]])
          }
          placeholder={`From`}
          className="form-control form-control-sm"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] || '')}
          max={Number(column.getFacetedMinMaxValues()?.[1] || '')}
          value={columnFilterValue?.[1] || ''}
          onChange={value =>
            column.setFilterValue(old => [old?.[0], value])
          }
          placeholder={`To`}
          className="form-control form-control-sm"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'date-range' ? (
    <div>
      <div className="d-flex space-x-2">
      <input
        type="date"
        value={columnFilterValue?.[0] || ''}
        onChange={e =>
          column.setFilterValue(old => [e.target.value || undefined, old?.[1]])
        }
        placeholder="Start Date"
        className="form-control form-control-sm"
      />

      <input
        type="date"
        value={columnFilterValue?.[1] || ''}
        onChange={e =>
          column.setFilterValue(old => [old?.[0], e.target.value || undefined])
        }
        placeholder="End Date"
        className="form-control form-control-sm"
      />

      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      aria-label={`Filter ${column.columnDef.header}`}
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString() ?? ''}
      className="form-select form-select-sm"
    >
      <option value="">All</option>
      {selectOptions.map(option => (
        <option value={option.value} key={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  ) : (
    <>
      <datalist id={column.id + 'list'}>
        {sortedUniqueValues.map(value => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        aria-label={`Filter ${column.columnDef.header}`}
        value={columnFilterValue || ''}
        onChange={value => column.setFilterValue(value)}
        placeholder={`Search... `}
        className="form-control form-control-sm"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
}
